import React, {FC, useEffect, useRef, useState} from 'react';
import styled, {css} from 'src/theme';
import {Col, Row, Table, Typography, Form, Input, Checkbox, Select, message} from "antd";
import InputMask from 'react-input-mask';
import generatePDF, {generatePDFFile} from 'src/utils/generatePDF'
import {Button} from 'src/components/UI/Button'
import useMediaQuery from "use-media-antd-query";
import {useStoreon} from 'storeon/react';
import {IEvents, IState} from 'src/store';
import {submitContactForm} from 'src/api';

import {usePresenter} from "./usePresenter";
import {getRoofImage} from "src/components/Roof/utils/getRoofImage";
import {drawAllDimensions} from "src/components/Roof/utils/drawDimensions";
import {defaultColors} from "src/parameters/colors";

import {roundToHundredths} from "src/utils/math";

interface Props {
}

export const Step6: FC<Props> = () => {
    const presenter = usePresenter();
    const {roofStore, appStore} = useStoreon<IState, IEvents>('roofStore', 'appStore');
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const colSize = useMediaQuery();

    const isMobile = colSize === 'xs' || colSize === 'sm' || colSize === 'md';

    const showContactForm = presenter.roofType !== 'multipitch';

    const pdfRoofImg = getRoofImage(
        presenter.profileType,
        presenter.metalTileName,
        presenter.pailingName,
        presenter.roofType,
    );
    const pdfRoofColor = defaultColors.find(color => color.id === roofStore.colorID)?.hexCode;

    const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        if (pdfCanvasRef.current && presenter.roofType) {
            drawAllDimensions(
                pdfCanvasRef.current,
                presenter.roofType,
                roofStore as unknown as Record<string, number | null>,
            );
        }
    }, [presenter.roofType, roofStore]);

    const handleSubmit = async (values: any) => {
        setSubmitting(true);
        try {
            const pdfFile = await generatePDFFile();
            await submitContactForm({
                name: values.name,
                phone: values.phone,
                email: values.email,
                city_id: values.city_id,
                comment: values.comment || '',
                consent: values.consent,
                form: `roof-${presenter.roofType}`,
                files: pdfFile ? [pdfFile] : undefined,
                items,
                total: totalPrice,
            });
            setSubmitted(true);
        } catch (e: any) {
            const errors = e?.response?.data?.errors;
            if (errors) {
                const fields = Object.keys(errors).map((key) => ({
                    name: key,
                    errors: errors[key],
                }));
                form.setFields(fields);
            } else {
                message.error('Произошла ошибка. Попробуйте позже.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const tableColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '50%',
            render: (text: any) => {
                return {
                    children: <TextBoldStyled>{text}</TextBoldStyled>,
                    props: {colSpan: 1},
                }
            },
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '12%'
        },
        {
            title: 'PPU',
            dataIndex: 'ppu',
            key: 'ppu',
            width: '18%'
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: '20%'
        },
    ];

    const selectedColor = defaultColors.find(c => c.id === roofStore.colorID);
    const colorLabel = selectedColor ? `${selectedColor.description1} ${selectedColor.description2}` : '';
    const roofProfileName = presenter.profileType === 'Профнастил'
        ? `Профнастил ${presenter.pailingName}`
        : `Металлочерепица ${presenter.metalTileName}`;
    const roofFullName = [
        roofProfileName,
        presenter.coatingName,
        presenter.thicknessName,
        colorLabel,
    ].filter(Boolean).join(', ');

    const productList = [
        {
            id: 1,
            name: roofFullName,
            token: 'N_kr',
            ppu: roundToHundredths(presenter.productRoof?.price ?? 0),
            unit: presenter.productRoof?.unit,
        },
        {
            id: 2,
            name: 'Планка конька, 2 м',
            token: 'N_kon',
            ppu: roundToHundredths(presenter.productRidgeCap?.price ?? 0),
            unit: presenter.productRidgeCap?.unit,
        },
        {
            id: 3,
            name: 'Планка карниза, 2 м',
            token: 'N_karn',
            ppu: roundToHundredths(presenter.productCornicePlank?.price ?? 0),
            unit: presenter.productCornicePlank?.unit,
        },
        {
            id: 4,
            name: 'Торцевая планка, 2 м',
            token: 'N_tor',
            ppu: roundToHundredths(presenter.productEndStrap?.price ?? 0),
            unit: presenter.productEndStrap?.unit,
        },
        {
            id: 5,
            name: 'Саморез 4,8х35',
            token: 'N_sa35',
            ppu: roundToHundredths(presenter.productScrew35?.price ?? 0),
            unit: presenter.productScrew35?.unit,
        },
        {
            id: 6,
            name: 'Саморез 4,8х70',
            token: 'N_sa70',
            ppu: roundToHundredths(presenter.productScrew70?.price ?? 0),
            unit: presenter.productScrew70?.unit,
        },
    ];

    const { tableData, totalPrice, items } = productList.reduce((acc:any, product) => {
        if (presenter.roofCalculations?.materials && presenter.roofCalculations.materials[product.token]) {
            const qty = presenter.roofCalculations.materials[product.token];
            const price = product.ppu ? roundToHundredths(qty * product.ppu) : 0;
            acc.tableData.push({
                key: product.id,
                name: product.name,
                quantity: `${qty} ${product.unit}`,
                ppu: `${product?.ppu} руб./${product?.unit}`,
                price: `${price} руб.`,
            });
            acc.items.push({
                name: product.name,
                quantity: qty,
                unit: product.unit || '',
                price: product.ppu ?? 0,
            });

            acc.totalPrice += price;
        }
        return acc;
    }, {tableData: [], totalPrice: 0, items: []});
    return (
        <RowStyled justify='center' gutter={[0, 40]}>

            <PDFHiddenStyled id={"pdf_hidden"}>
                <PDFLayoutStyled id={"pdf_for_save"}>

                    <PDFHeaderStyled>Предварительный расчет проекта</PDFHeaderStyled>

                    <PDFImageWrapStyled>
                        <PDFImageInner>
                            <PDFRoofImg src={pdfRoofImg} bgColor={pdfRoofColor} alt="" />
                            <PDFCanvas ref={pdfCanvasRef} width={1920} height={1080} />
                        </PDFImageInner>
                    </PDFImageWrapStyled>

                    <Row justify='center'>
                        <ColTableStyled is_mobile={0}>

                            <Table
                              bordered={false}
                              pagination={false}
                              showHeader={false}
                              dataSource={tableData}
                              columns={tableColumns}
                            />

                            <PDFTotalStyled>
                                Итого: {totalPrice} руб
                            </PDFTotalStyled>

                        </ColTableStyled>
                        <PDFRemarkStyled>
                            <Typography.Paragraph>
                                Вся приведенная информация,
                                включая информацию о ценах, носит исключительно рекламно-информационный характер
                                и не является публичной офертой. Точную информацию о ценах и ассортименте уточняйте
                                в офисах продаж и по телефонам.
                            </Typography.Paragraph>
                        </PDFRemarkStyled>

                    </Row>
                </PDFLayoutStyled>
            </PDFHiddenStyled>

            <Col span={24}>
                <HeaderStyled>Ознакомьтесь с Вашими результатами расчета</HeaderStyled>

                <Row justify='center'>
                    <ColTableStyled is_mobile={isMobile ? 1 : 0} xs={22}>

                        <Table
                            bordered={false}
                            pagination={false}
                            showHeader={false}
                            dataSource={tableData}
                            columns={tableColumns}
                            scroll={{y: 300}}
                        />

                        <DivStyled>
                            Итого: {totalPrice} руб
                        </DivStyled>

                    </ColTableStyled>
                </Row>
            </Col>

            {showContactForm ? (
                <Col span={24}>
                    <Row justify='center'>
                        <ColTableStyled is_mobile={isMobile ? 1 : 0} xs={22}>
                            <ContactFormWrapper>
                                {submitted ? (
                                    <SuccessMessage>
                                        <SuccessTitle>Заявка отправлена!</SuccessTitle>
                                        <SuccessText>
                                            Наш менеджер свяжется с вами в ближайшее время
                                            для уточнения деталей заказа.
                                        </SuccessText>
                                    </SuccessMessage>
                                ) : (
                                    <>
                                        <ContactFormHeader>Оставить заявку</ContactFormHeader>
                                        <Form
                                            form={form}
                                            layout="vertical"
                                            onFinish={handleSubmit}
                                        >
                                            <Form.Item
                                                name="name"
                                                label="Имя"
                                                rules={[{ required: true, message: 'Укажите имя' }]}
                                            >
                                                <Input placeholder="Ваше имя" />
                                            </Form.Item>

                                            <Form.Item
                                                name="phone"
                                                label="Телефон"
                                                rules={[
                                                    { required: true, message: 'Укажите телефон' },
                                                    {
                                                        pattern: /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
                                                        message: 'Введите полный номер телефона',
                                                    },
                                                ]}
                                            >
                                                <InputMask mask="+7 (999) 999-99-99">
                                                    {/* @ts-ignore */}
                                                    {(inputProps: any) => <Input {...inputProps} placeholder="+7 (___) ___-__-__" />}
                                                </InputMask>
                                            </Form.Item>

                                            <Form.Item
                                                name="email"
                                                label="Email"
                                                rules={[
                                                    { required: true, message: 'Укажите email' },
                                                    { type: 'email', message: 'Укажите корректный email' },
                                                ]}
                                            >
                                                <Input placeholder="email@example.com" />
                                            </Form.Item>

                                            <Form.Item
                                                name="city_id"
                                                label="Город"
                                                rules={[{ required: true, message: 'Выберите город' }]}
                                            >
                                                <Select
                                                    placeholder="Выберите город"
                                                    showSearch
                                                    optionFilterProp="label"
                                                    options={appStore.cities.map((c) => ({
                                                        value: c.id,
                                                        label: c.name,
                                                    }))}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                name="comment"
                                                label="Комментарий"
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="Ваши пожелания или вопросы"
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                name="consent"
                                                valuePropName="checked"
                                                rules={[
                                                    {
                                                        validator: (_, value) =>
                                                            value
                                                                ? Promise.resolve()
                                                                : Promise.reject('Необходимо согласие на обработку данных'),
                                                    },
                                                ]}
                                            >
                                                <Checkbox
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            form.setFields([{ name: 'consent', errors: [] }]);
                                                        }
                                                    }}
                                                >
                                                    Даю согласие на обработку персональных данных
                                                </Checkbox>
                                            </Form.Item>

                                            <ButtonsRow>
                                                <Button
                                                    onClick={() => form.submit()}
                                                    disabled={submitting}
                                                >
                                                    {submitting ? 'Отправка...' : 'Отправить заявку'}
                                                </Button>
                                                <Button onClick={generatePDF}>Скачать PDF</Button>
                                            </ButtonsRow>
                                        </Form>
                                    </>
                                )}
                            </ContactFormWrapper>
                        </ColTableStyled>
                    </Row>
                </Col>
            ) : (
                <Button onClick={generatePDF}>Скачать PDF</Button>
            )}
        </RowStyled>
    );
};

const PDFHiddenStyled = styled('div')`
    position: relative;
    top: 0;
    left: 0;
    overflow: hidden;
`;
const PDFLayoutStyled = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1000;
    padding: 10px;
    width: 500px;
    height: 650px;
    background-color: white;
    font-size: 10px;
`;

const PDFRemarkStyled = styled(Col)`
  margin-top: 20px;
  font-size: 9px;
  color:  ${props => props.theme.colors.smoke};
`;

const PDFHeaderStyled = styled.h4`
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  margin-bottom: 15px;
  color: ${props => props.theme.colors.orange};
`;

const PDFImageWrapStyled = styled('div')`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

const PDFImageInner = styled('div')`
  position: relative;
  width: 280px;
  line-height: 0;
`;

const PDFRoofImg = styled.img<{ bgColor?: string }>`
  width: 100%;
  height: auto;
  display: block;
  background-color: ${props => props.bgColor || 'transparent'};
`;

const PDFCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const PDFTotalStyled = styled('div')`
  font-size: 16px;
  text-align: right;
  padding: 10px;
`;


const RowStyled = styled(Row)`
  padding: 25px 10px;
  background: ${props => props.theme.colors.smoke};
  border-radius: 6px;
`;

const DivStyled = styled('div')`
  text-align: right;
  padding: 10px;
`;

const ColTableStyled = styled(Col)<{ is_mobile: number }>`
  ${(props) => css`
    & .ant-table-wrapper table {
      font-size: ${props.is_mobile ? '10px' : '12px'};
    }
    & .ant-table-wrapper td {
      padding: ${props.is_mobile ? '2px' : '5px'};
    }
  `}
`;

const HeaderStyled = styled.h4`
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  margin-bottom: 15px;
  color: ${props => props.theme.colors.nero};
`;

const TextBoldStyled = styled.div`
  font-weight: bold;
`;

const ContactFormWrapper = styled.div`
  padding: 16px 0;
`;

const ContactFormHeader = styled.h4`
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.nero};
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const SuccessTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #52c41a;
  margin-bottom: 12px;
`;

const SuccessText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
`;
